/**
 * Per-prop attributes: the pack's tint, and the drawn art's recipe.
 *
 * Baked into the persisted record, because the renderer reads only what the
 * object carries (`proceduralRenderInput`), never the style table.
 */

/** Every prop that draws the pack's ART motif. Matched by set, not by a name test. */
const ART_TYPES = new Set(['staging-art', 'staging-art-leaned']);

/**
 * Kitchen props whose MODEL depends on the style — the fruit in the bowl, the
 * tray, the mills, the pasta. They carry the style id as `variant`, and the
 * renderer looks the style's kitchen block up (`kitchenLookFor`), so the
 * planner and the drawing read one table.
 */
const STYLED_KITCHEN_TYPES = new Set([
  'staging-fruit-bowl',
  'staging-vase-tulips',
  'staging-dish-soap-set',
  'staging-cutting-boards',
  'staging-utensil-crock',
  'staging-tea-towel',
  'staging-dish-towel-folded',
  'staging-herb-pot',
  'staging-cookbook-stand',
  'staging-mills',
  'staging-canisters',
]);

/** Every prop tinted with the pack's TOWEL colour rather than its accent. */
const isTowelTinted = (type) => type.startsWith('staging-towel');

function attributesFor(type, style) {
  if (ART_TYPES.has(type)) {
    const art = style?.art || {};
    return {
      artMotif: art.motif,
      artColors: art.colors,
      // Stamped, never derived from the id: persisting re-ids every object, so
      // an id-derived seed would redraw all the art on save.
      artSeed: art.seed,
    };
  }
  if (STYLED_KITCHEN_TYPES.has(type)) {
    return {
      variant: style?.id || null,
      ...(style?.accent ? { themeColor: style.accent } : {}),
    };
  }
  if (isTowelTinted(type)) {
    return style?.towel ? { themeColor: style.towel } : {};
  }
  return style?.accent ? { themeColor: style.accent } : {};
}

module.exports = { ART_TYPES, STYLED_KITCHEN_TYPES, attributesFor };
